import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { adminProductsApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";

const STATUSES = ["draft", "active", "out_of_stock", "archived"];
const TAX_CODES = ["standard", "zero_rated", "exempt", "out_of_scope"];

const EMPTY = {
  sku: "", slug: "", name: "", brand: "", category_id: "", description: "",
  price_aed: "0.00", compare_at_aed: "",
  is_stock_available: true,
  product_type: "aftermarket",
  status: "active", is_featured: false,
  vat_rate: 0.05, tax_inclusive: false, tax_code: "standard",
  images: [], // [{url, thumb_url, is_main, sort_order, alt}]
};

function toForm(p) {
  const rawImgs = (p.images || []).slice();
  const images = rawImgs
    .map((im, i) => ({
      url: im.url,
      thumb_url: im.thumb_url || im.url,
      is_main: !!im.is_main,
      sort_order: im.sort_order ?? i,
      alt: im.alt || "",
    }))
    .sort((a, b) => a.sort_order - b.sort_order);
  // Guarantee exactly one main image.
  if (images.length && !images.some((i) => i.is_main)) images[0].is_main = true;
  return {
    ...EMPTY, ...p,
    price_aed: (p.price?.amount_minor ?? 0) / 100 + "",
    compare_at_aed: p.compare_at_price ? (p.compare_at_price.amount_minor / 100 + "") : "",
    vat_rate: p.tax?.vat_rate ?? 0.05,
    tax_inclusive: !!p.tax?.tax_inclusive,
    tax_code: p.tax?.tax_code || "standard",
    is_stock_available: p.is_stock_available === undefined ? true : !!p.is_stock_available,
    product_type: p.product_type || "aftermarket",
    images,
    category_id: p.category_id || "",
  };
}

function buildPayload(f, forCreate) {
  const price_minor = Math.round(parseFloat(f.price_aed || "0") * 100);
  const compare_minor = f.compare_at_aed ? Math.round(parseFloat(f.compare_at_aed) * 100) : null;
  const images = (f.images || []).map((im, i) => ({
    url: im.url,
    thumb_url: im.thumb_url || im.url,
    is_main: !!im.is_main,
    sort_order: i,
    alt: im.alt || null,
  }));
  // Ensure exactly one main.
  if (images.length && !images.some((i) => i.is_main)) images[0].is_main = true;
  const base = {
    name: f.name, brand: f.brand || null,
    category_id: f.category_id || null,
    description: f.description || null,
    specifications: f.specifications || {},
    price: { amount_minor: price_minor, currency: "AED" },
    compare_at_price: compare_minor ? { amount_minor: compare_minor, currency: "AED" } : null,
    tax: { vat_rate: parseFloat(f.vat_rate) || 0.05, tax_inclusive: !!f.tax_inclusive, tax_code: f.tax_code },
    is_stock_available: !!f.is_stock_available,
    product_type: f.product_type === "genuine" ? "genuine" : "aftermarket",
    status: f.status, is_featured: !!f.is_featured,
    tags: f.tags || [], images,
  };
  if (forCreate) return { sku: f.sku, slug: f.slug || undefined, ...base };
  return base;
}

export default function AdminProductEdit() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    let alive = true;
    adminProductsApi.categories().then((c) => alive && setCats(c));
    if (!isNew) {
      adminProductsApi.get(id).then((p) => alive && setForm(toForm(p))).catch((e) => setError(e.response?.data?.error?.message || "Load failed"));
    }
    return () => { alive = false; };
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (isNew) {
        await adminProductsApi.create(buildPayload(form, true));
      } else {
        await adminProductsApi.update(id, buildPayload(form, false));
      }
      nav("/admin/products", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error?.message || "Save failed");
    } finally { setBusy(false); }
  };

  const archive = async () => {
    if (!id) return;
    if (!window.confirm("Archive this product? It will hide from the catalogue.")) return;
    setBusy(true);
    try { await adminProductsApi.archive(id); nav("/admin/products"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm(
      "Delete this product permanently?\n\n" +
      "If it has been part of any order, it will be archived instead so " +
      "past orders and invoices keep working.",
    )) return;
    setBusy(true);
    setError("");
    try {
      const res = await adminProductsApi.remove(id);
      if (res?.message) window.alert(res.message);
      nav("/admin/products");
    } catch (err) {
      setError(err.response?.data?.error?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setError("Please choose an image file (JPG, PNG or WEBP).");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setError("Image is larger than 6 MB. Please pick a smaller one.");
      return;
    }
    setError("");
    setUploadPct(0);
    setUploading(true);
    try {
      const res = await adminProductsApi.uploadImage(file, setUploadPct);
      setForm((f) => {
        const next = [
          ...(f.images || []),
          {
            url: res.url,
            thumb_url: res.url,
            is_main: (f.images || []).length === 0,
            sort_order: (f.images || []).length,
            alt: file.name.replace(/\.[^.]+$/, ""),
          },
        ];
        return { ...f, images: next };
      });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const addImageByUrl = (url) => {
    const trimmed = (url || "").trim();
    if (!trimmed) return;
    setForm((f) => {
      const already = (f.images || []).some((im) => im.url === trimmed);
      if (already) return f;
      const next = [
        ...(f.images || []),
        {
          url: trimmed,
          thumb_url: trimmed,
          is_main: (f.images || []).length === 0,
          sort_order: (f.images || []).length,
          alt: "",
        },
      ];
      return { ...f, images: next };
    });
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const arr = [...(f.images || [])];
      arr.splice(idx, 1);
      if (arr.length && !arr.some((i) => i.is_main)) arr[0].is_main = true;
      return { ...f, images: arr.map((im, i) => ({ ...im, sort_order: i })) };
    });
  };

  const setMainImage = (idx) => {
    setForm((f) => ({
      ...f,
      images: (f.images || []).map((im, i) => ({ ...im, is_main: i === idx })),
    }));
  };

  const moveImage = (idx, dir) => {
    setForm((f) => {
      const arr = [...(f.images || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, images: arr.map((im, i) => ({ ...im, sort_order: i })) };
    });
  };

  const updateImageAlt = (idx, alt) => {
    setForm((f) => ({
      ...f,
      images: (f.images || []).map((im, i) => (i === idx ? { ...im, alt } : im)),
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Products</div>
            <div className="text-lg font-semibold">{isNew ? "New product" : "Edit product"}</div>
          </div>
          <Button asChild variant="outline"><Link to="/admin/products">&larr; Products</Link></Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10">
        <form onSubmit={save} className="space-y-6" data-testid="admin-product-form" noValidate>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <Card>
            <CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Part number" required>
                  <Input value={form.sku} onChange={(e) => set("sku", e.target.value)} disabled={!isNew} required data-testid="pf-sku" placeholder="e.g. FF-BRK-1023" />
                </Field>
                <Field label="Product name" required>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="pf-name" />
                </Field>
                <Field label="Brand">
                  <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" />
                </Field>
                <Field label="Category">
                  <Select value={form.category_id || "none"} onValueChange={(v) => set("category_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="pf-category"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">(none)</SelectItem>
                      {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Description">
                <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Price &amp; VAT</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Price (AED)" required>
                  <Input type="number" step="0.01" min="0" value={form.price_aed} onChange={(e) => set("price_aed", e.target.value)} required data-testid="pf-price" />
                </Field>
                <Field label="Compare at (was) AED">
                  <Input type="number" step="0.01" min="0" value={form.compare_at_aed} onChange={(e) => set("compare_at_aed", e.target.value)} />
                </Field>
                <Field label="VAT rate">
                  <Input type="number" step="0.01" min="0" max="1" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid="pf-vat" />
                </Field>
                <Field label="Tax code">
                  <Select value={form.tax_code} onValueChange={(v) => set("tax_code", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TAX_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={!!form.tax_inclusive} onCheckedChange={(v) => set("tax_inclusive", v)} />
                  <span className="text-sm">Price is tax-inclusive</span>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={!!form.is_featured} onCheckedChange={(v) => set("is_featured", v)} />
                  <span className="text-sm">Feature on homepage</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Preview: {formatMoney({ amount_minor: Math.round(parseFloat(form.price_aed || "0") * 100), currency: "AED" })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Availability</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Availability" required>
                <Select
                  value={form.is_stock_available ? "available" : "unavailable"}
                  onValueChange={(v) => set("is_stock_available", v === "available")}
                >
                  <SelectTrigger data-testid="pf-availability"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="unavailable">Not available</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Product type" required>
                <Select
                  value={form.product_type}
                  onValueChange={(v) => set("product_type", v)}
                >
                  <SelectTrigger data-testid="pf-product-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="genuine">Genuine / OEM</SelectItem>
                    <SelectItem value="aftermarket">Aftermarket</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="text-xs text-muted-foreground sm:col-span-2">
                Availability controls whether customers can order this part. Product type shows customers whether it is a Genuine/OEM manufacturer part or an Aftermarket replacement.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Photos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>Add images from device</Label>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <input
                    id="pf-image-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      // Upload sequentially to keep progress simple.
                      (async () => {
                        for (const f of files) {
                          // eslint-disable-next-line no-await-in-loop
                          await uploadImage(f);
                        }
                      })();
                      e.target.value = "";
                    }}
                    className="hidden"
                    data-testid="pf-image-file"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById("pf-image-file")?.click()}
                    disabled={uploading}
                    data-testid="pf-image-upload-btn"
                  >
                    {uploading ? `Uploading… ${uploadPct}%` : "📁 Choose images"}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG or WEBP. Up to 6&nbsp;MB each. Add as many as you like.
                  </span>
                </div>
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>…or paste an image URL</Label>
                  <Input
                    placeholder="https://…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addImageByUrl(e.currentTarget.value);
                        e.currentTarget.value = "";
                      }
                    }}
                    data-testid="pf-image-url"
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={(e) => {
                    const inp = e.currentTarget.parentElement.querySelector("input");
                    if (inp) { addImageByUrl(inp.value); inp.value = ""; }
                  }}
                >
                  Add URL
                </Button>
              </div>

              {(form.images || []).length === 0 ? (
                <div className="text-xs text-muted-foreground border border-dashed rounded p-6 text-center">
                  No images yet. Upload from your device or paste a URL to get started.
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="pf-image-list">
                  {form.images.map((im, i) => (
                    <div key={`${im.url}-${i}`} className="border rounded-lg p-3 flex gap-3">
                      <div className="w-24 h-24 flex-none rounded overflow-hidden bg-muted relative">
                        <img src={im.thumb_url || im.url} alt="" className="w-full h-full object-cover" />
                        {im.is_main && (
                          <span className="absolute top-1 left-1 text-[10px] bg-black text-white px-1.5 py-0.5 rounded">
                            Main
                          </span>
                        )}
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <Input
                          value={im.alt || ""}
                          onChange={(e) => updateImageAlt(i, e.target.value)}
                          placeholder="Alt text (optional)"
                          className="h-8 text-xs"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {!im.is_main && (
                            <Button type="button" size="sm" variant="outline"
                              onClick={() => setMainImage(i)}
                              data-testid={`pf-image-set-main-${i}`}>
                              Set as main
                            </Button>
                          )}
                          <Button type="button" size="sm" variant="ghost"
                            onClick={() => moveImage(i, -1)} disabled={i === 0}>↑</Button>
                          <Button type="button" size="sm" variant="ghost"
                            onClick={() => moveImage(i, 1)} disabled={i === form.images.length - 1}>↓</Button>
                          <Button type="button" size="sm" variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeImage(i)}
                            data-testid={`pf-image-remove-${i}`}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />
          <div className="flex gap-3 flex-wrap">
            <Button type="submit" disabled={busy} data-testid="pf-save">{busy ? "Saving…" : "Save"}</Button>
            {!isNew && (
              <>
                <Button type="button" variant="outline" onClick={archive} disabled={busy} data-testid="pf-archive">
                  Archive
                </Button>
                <Button type="button" variant="destructive" onClick={remove} disabled={busy} data-testid="pf-delete">
                  Delete
                </Button>
              </>
            )}
            <Button type="button" variant="ghost" asChild><Link to="/admin/products">Cancel</Link></Button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  );
}
